/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';
import { compose } from 'recompose';

import connectToStores from 'js/lib/connectToStores';
import Retracked from 'js/lib/retracked';
import useRouter, { useLocation } from 'js/lib/useRouter';

import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import CsrfTokenContext from 'bundles/page/contexts/CsrfTokenContext';
import RouterContext from 'bundles/page/contexts/RouterContext';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import InteractionGuard from 'bundles/unified-common/components/InteractionGuard';
import {
  GlobalCoachChatStateProvider,
  useGlobalCoachChatState,
} from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import NavAiSparkleExperimentProvider, {
  useNavAiSparkle,
} from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';

import 'css!bundles/partners-graphql/styl/all';

const GlobalCoachChat = React.lazy(
  () => import('bundles/unified-common/components/coach-on-discovery/GlobalCoachChat')
);

const styles = {
  wrapperStyles: css`
    .max-width-980.m-t-3 {
      margin: 48px auto 0;
      max-width: 980px;
    }
  `,
};

type PropsFromStores = {
  csrfToken?: string | null;
};

type PropsFromCaller = {
  children: React.ReactNode;
};

type PropsToComponent = PropsFromStores & PropsFromCaller;

const PartnerAppContent = ({ children }: React.PropsWithChildren) => {
  const { showNavAiSparkle } = useNavAiSparkle();
  const { isContextProvided, coachOpen, pageChatType } = useGlobalCoachChatState();
  const isFullscreenCoachChatOpen = isContextProvided && coachOpen && pageChatType === null;

  return (
    <div className="rc-PartnerApp">
      <DefaultHeader showMetaNav />
      <UniversalPageFeatures />
      {showNavAiSparkle && (
        <React.Suspense fallback={null}>
          <GlobalCoachChat />
        </React.Suspense>
      )}
      <InteractionGuard preventInteraction={isFullscreenCoachChatOpen}>
        {children}
        <PageFooter />
      </InteractionGuard>
    </div>
  );
};

const PartnerApp = ({ children, csrfToken }: PropsToComponent) => {
  const router = useRouter();
  const { pathname } = useLocation();
  const isPartnersPage = pathname.startsWith('/partners') || pathname.startsWith('/about/partners/preview');

  return (
    <StatsigProvider>
      <div css={styles.wrapperStyles}>
        <RouterContext.Provider value={router}>
          <CsrfTokenContext.Provider value={csrfToken || '<MISSING>'}>
            {isPartnersPage ? (
              <GlobalCoachChatStateProvider>
                <NavAiSparkleExperimentProvider>
                  <PartnerAppContent>{children}</PartnerAppContent>
                </NavAiSparkleExperimentProvider>
              </GlobalCoachChatStateProvider>
            ) : (
              <PartnerAppContent>{children}</PartnerAppContent>
            )}
          </CsrfTokenContext.Provider>
        </RouterContext.Provider>
      </div>
    </StatsigProvider>
  );
};

export default compose<PropsToComponent, PropsFromStores>(
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        app: 'partners',
      },
    };
  }),
  connectToStores<PropsFromStores, PropsFromCaller, ApplicationStoreClass>(
    [ApplicationStoreClass],
    (ApplicationStore, props) => ({
      ...props,
      csrfToken: ApplicationStore.getState().csrfToken,
    })
  )
)(PartnerApp);
